import { opaqueHex } from '@/nova/theme/themePalette';
import { COLORS } from '@/nova/theme/tokens';

import type { PlayableParams } from './playable-api';

/**
 * The game picker's demos are drawn in the Studio's own colours, not the brand's: the art a run paints (the art style,
 * the promotion's key art) recolours the finished game anyway, so brand colours in the picker promise a look the game
 * may not have (a green demo for what generates red). The Studio's tokens are CSS variables that follow its dark / light
 * theme; the demo runs in a sandboxed frame that cannot read them, so they are resolved to hex here. The demo is grey
 * with orange highlights (the Studio's surfaces and its CTA orange), never the Studio's blue accent.
 */

/** The Studio colours a demo is drawn in (hex). */
export interface StudioColours {
  bg: string;
  text: string;
  primary: string;
  accent: string;
  ctaFill: string;
  ctaText: string;
}

/** A token value (`var(--ows-…)` or a literal colour) as `#rrggbb`, reading a variable through `read`; '' when it
 *  cannot be resolved. Pure. */
export const resolveTokenHex = (token: string, read: (name: string) => string): string => {
  const m = /^var\((--[^,)\s]+)/.exec(token.trim());
  return opaqueHex(m ? read(m[1]) : token);
};

/** The Studio's current colours, read from the document (its theme and accent as they are now); null outside a browser
 *  or when a token cannot be resolved (the demo then keeps the brand's colours). */
export const readStudioColours = (): StudioColours | null => {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') return null;
  const style = getComputedStyle(document.documentElement);
  const read = (name: string): string => style.getPropertyValue(name);
  const out: StudioColours = {
    bg: resolveTokenHex(COLORS.surfaceCard, read),
    text: resolveTokenHex(COLORS.textBright, read),
    // Grey with orange highlights (the Studio's CTA orange), never its blue accent.
    primary: resolveTokenHex(COLORS.textFaint, read),
    accent: resolveTokenHex(COLORS.ctaSolid, read),
    ctaFill: resolveTokenHex(COLORS.ctaSolid, read),
    ctaText: resolveTokenHex(COLORS.ctaSolidText, read),
  };
  return Object.values(out).every(Boolean) ? out : null;
};

/** A picker demo's document in the Studio's colours: the palette and the CTA button from `colours`, the brand's fonts
 *  dropped. On the light theme the brand's logo (a white wordmark on ads) would vanish, so the demo shows the brand's
 *  name in the text colour instead. Pure. */
export const withStudioColours = (
  params: PlayableParams,
  colours: StudioColours,
  mode: 'light' | 'dark',
): PlayableParams => {
  const { font_heading: _heading, font_body: _body, ...brand } = params.brand;
  return {
    ...params,
    brand: {
      ...brand,
      ...(mode === 'light' ? { logo_url: '' } : {}),
      palette: { primary: colours.primary, accent: colours.accent, bg: colours.bg, text: colours.text },
      button: { fill: colours.ctaFill, text: colours.ctaText, border: '', radius_px: 999, pill: true },
    },
  };
};
